import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { assertOfficeScheduleAccess } from '@/lib/auth/officeScheduleGuard';
import { ChevronLeft, ChevronRight, CloudRain, Loader2 } from 'lucide-react';
import { addDays, format, subDays } from 'date-fns';
import { Button } from '@/components/atoms/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/atoms/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  formatDateTimeInTimeZone,
  formatTimeZoneLabel,
  timeZoneAbbrev,
  getViewerTimeZone,
} from '@/lib/date-values';
import { timeZonesMatch } from '@/lib/time/scheduleTimezone';
import { ScheduleWorkspaceShell } from '@/components/layouts';
import { RoutesMapView } from '@/components/dispatch/RoutesMapView';
import { RoutesOverview } from '@/components/dispatch/RoutesOverview';
import { WeatherHoldFlowPanel } from '@/components/dispatch/WeatherHoldFlowPanel';
import { BookingDetailSheet } from '@/components/calendar/BookingDetailSheet';
import { COLOR_BY_VALUES, DEFAULT_COLOR_BY, type ColorBy } from '@/components/dispatch/tech-colors';
import { useHasPermission } from '@/hooks/usePermissions';
import { useAllSchedulerBookings } from '@/hooks/scheduler/useSchedulerBookings';
import { useTerritories } from '@/hooks/territories/useTerritories';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import {
  useRouteSummaries,
  type RouteSummaryFilters,
} from '@/hooks/scheduler/useRouteOptimization';
import type {
  AppointmentStatus,
  BookingListParams,
  JobPriority,
  SchedulerBooking,
} from '@/types/scheduler';

type RoutesTab = 'overview' | 'map';
const PRIORITY_VALUES: JobPriority[] = ['EMERGENCY', 'HIGH', 'NORMAL', 'LOW'];
const ROUTE_MAP_EXCLUDED_STATUSES = new Set<AppointmentStatus>([
  'CANCELLATION_PENDING',
  'CANCELLED',
  'SKIPPED',
  'EXPIRED',
]);

function toDateString(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}
/** "Today" (YYYY-MM-DD) in the business tz — routes render in business time. */
function todayInTz(tz: string | null): string {
  if (!tz) return toDateString(new Date());
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
function isRouteMapBooking(booking: SchedulerBooking): boolean {
  return !ROUTE_MAP_EXCLUDED_STATUSES.has(booking.status);
}

export const Route = createFileRoute('/_dashboard/dashboard/routes')({
  beforeLoad: () => assertOfficeScheduleAccess(),
  validateSearch: (search: Record<string, unknown>) => ({
    date:
      typeof search.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.date)
        ? search.date
        : undefined,
    // NOT-329 — mirrors Dispatch: an absent/invalid param stays undefined so it
    // never gets pinned into the URL, and DEFAULT_COLOR_BY applies at the point
    // of use. The whitelist has to list `jobtype` too, or an explicit
    // ?colorBy=jobtype survives by luck.
    colorBy: COLOR_BY_VALUES.includes(search.colorBy as ColorBy)
      ? (search.colorBy as ColorBy)
      : undefined,
    ...(search.routesTab === 'map' || search.routesTab === 'overview'
      ? { routesTab: search.routesTab as RoutesTab }
      : {}),
    bookingId: typeof search.bookingId === 'string' ? search.bookingId : undefined,
    territoryId: typeof search.territoryId === 'string' ? search.territoryId : undefined,
    jobTypeId: typeof search.jobTypeId === 'string' ? search.jobTypeId : undefined,
    priority: PRIORITY_VALUES.includes(search.priority as JobPriority)
      ? (search.priority as JobPriority)
      : undefined,
    exceptions: search.exceptions === 'true' ? true : undefined,
  }),
  component: RoutesPage,
});

/** Routes — recurring/route planning surface (promoted out of Dispatch). The
 *  day's routes as a capacity Overview + a Map with per-route stops. Sibling of
 *  Dispatch/Calendar under Schedule. */
function RoutesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: businessSettings,
    isError: settingsError,
    refetch: refetchSettings,
  } = useBusinessSettings();
  const businessTz = businessSettings?.timezone ?? null;

  const hasExplicitDate = Boolean(search.date);
  const dateReady = businessTz !== null;
  const currentDate = search.date ?? todayInTz(businessTz);
  const colorBy = search.colorBy ?? DEFAULT_COLOR_BY;
  const routesTab = search.routesTab ?? 'overview';
  const dateObj = useMemo(() => parseLocalDate(currentDate), [currentDate]);
  const isToday = currentDate === todayInTz(businessTz);

  // Pin the implicit date into the URL as the business-tz day once the tz is known.
  useEffect(() => {
    if (!hasExplicitDate && businessTz) {
      void navigate({
        replace: true,
        search: (prev) => ({ ...prev, date: todayInTz(businessTz) }),
      });
    }
  }, [hasExplicitDate, businessTz, navigate]);

  const canManageWeatherHold = useHasPermission('dispatch:reassign');
  const { data: territories = [] } = useTerritories();

  const [selectedRouteTechnicianId, setSelectedRouteTechnicianId] = useState<string>();
  const [selectedBooking, setSelectedBooking] = useState<SchedulerBooking | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [weatherHoldOpen, setWeatherHoldOpen] = useState(false);
  const [weatherDrawActive, setWeatherDrawActive] = useState(false);
  const [weatherDrawRing, setWeatherDrawRing] = useState<google.maps.LatLngLiteral[] | null>(null);

  const routeSummaryFilters = useMemo<RouteSummaryFilters>(
    () => ({
      ...(search.territoryId ? { territoryId: search.territoryId } : {}),
      ...(search.jobTypeId ? { jobTypeId: search.jobTypeId } : {}),
      ...(search.priority ? { priority: search.priority } : {}),
      ...(search.exceptions ? { hasExceptions: true } : {}),
    }),
    [search.territoryId, search.jobTypeId, search.priority, search.exceptions],
  );
  const { data: routeSummaries = [], isFetching: routeSummariesLoading } = useRouteSummaries(
    currentDate,
    routeSummaryFilters,
    dateReady,
  );
  const activeRouteTechnicianId = useMemo(() => {
    if (
      selectedRouteTechnicianId &&
      routeSummaries.some((summary) => summary.technicianId === selectedRouteTechnicianId)
    ) {
      return selectedRouteTechnicianId;
    }
    return routeSummaries[0]?.technicianId;
  }, [routeSummaries, selectedRouteTechnicianId]);

  const routeMapBookingParams = useMemo<BookingListParams>(
    () => ({
      dateFrom: currentDate,
      dateTo: currentDate,
      ...(search.territoryId ? { territoryId: search.territoryId } : {}),
      ...(search.jobTypeId ? { jobTypeId: search.jobTypeId } : {}),
      ...(search.priority ? { priority: search.priority } : {}),
      ...(search.exceptions ? { hasExceptions: true } : {}),
    }),
    [currentDate, search.territoryId, search.jobTypeId, search.priority, search.exceptions],
  );
  const { data: routeMapBookings } = useAllSchedulerBookings(routeMapBookingParams, {
    // Fetch the day's bookings whenever the date is ready — NOT gated on a
    // selected route — so unplanned flex visits surface even before any route
    // exists (they're what the "Needs a route" list plans).
    enabled: dateReady,
  });
  const routeMapStops = useMemo(
    () => (routeMapBookings?.data ?? []).filter(isRouteMapBooking),
    [routeMapBookings?.data],
  );

  const viewerTz = getViewerTimeZone();
  const showViewerHint = Boolean(businessTz) && !timeZonesMatch(viewerTz, businessTz);
  const nowIso = new Date().toISOString();

  const setDate = useCallback(
    (next: string) => void navigate({ search: (prev) => ({ ...prev, date: next }) }),
    [navigate],
  );
  const setRoutesTab = useCallback(
    (next: RoutesTab) => void navigate({ search: (prev) => ({ ...prev, routesTab: next }) }),
    [navigate],
  );
  const setFacet = useCallback(
    (key: 'territoryId', value: string | undefined) => {
      void navigate({ search: (prev) => ({ ...prev, [key]: value || undefined }) });
    },
    [navigate],
  );

  const handleBookingClick = useCallback(
    (booking: SchedulerBooking) => {
      setSelectedBooking(booking);
      setDetailOpen(true);
      void navigate({ search: (prev) => ({ ...prev, bookingId: booking.bookingId }) });
    },
    [navigate],
  );
  const handleDetailOpenChange = useCallback(
    (open: boolean) => {
      setDetailOpen(open);
      if (!open) void navigate({ search: (prev) => ({ ...prev, bookingId: undefined }) });
    },
    [navigate],
  );

  const handleStartWeatherDraw = useCallback(() => {
    if (!canManageWeatherHold) return;
    setWeatherHoldOpen(true);
    setWeatherDrawActive(true);
    setWeatherDrawRing(null);
  }, [canManageWeatherHold]);
  const handleWeatherDrawComplete = useCallback(
    (ring: google.maps.LatLngLiteral[]) => {
      if (!canManageWeatherHold) return;
      setWeatherDrawRing(ring);
      setWeatherDrawActive(false);
      setWeatherHoldOpen(true);
    },
    [canManageWeatherHold],
  );
  const handleOpenWeatherHold = useCallback(() => {
    if (!canManageWeatherHold) return;
    setWeatherHoldOpen(true);
    setWeatherDrawActive(false);
  }, [canManageWeatherHold]);
  const handleCloseWeatherHold = useCallback(() => {
    setWeatherHoldOpen(false);
    setWeatherDrawActive(false);
    setWeatherDrawRing(null);
  }, []);

  useEffect(() => {
    if (!canManageWeatherHold && weatherHoldOpen) handleCloseWeatherHold();
  }, [canManageWeatherHold, handleCloseWeatherHold, weatherHoldOpen]);

  // Keep a valid selected route as summaries load/change.
  useEffect(() => {
    if (routeSummaries.length === 0) return;
    if (
      selectedRouteTechnicianId &&
      routeSummaries.some((summary) => summary.technicianId === selectedRouteTechnicianId)
    ) {
      return;
    }
    setSelectedRouteTechnicianId(routeSummaries[0]!.technicianId);
  }, [routeSummaries, selectedRouteTechnicianId]);

  // Deep-link: open the Sheet for ?bookingId once its booking is in scope.
  useEffect(() => {
    if (!search.bookingId) {
      if (selectedBooking) setSelectedBooking(null);
      return;
    }
    if (selectedBooking?.bookingId === search.bookingId) return;
    const match = routeMapStops.find((b) => b.bookingId === search.bookingId);
    if (match) {
      setSelectedBooking(match);
      setDetailOpen(true);
    }
  }, [search.bookingId, selectedBooking, routeMapStops]);

  if (!dateReady) {
    return (
      <ScheduleWorkspaceShell hideHeader>
        <div className="flex h-full flex-col items-center justify-center gap-3 bg-background">
          {settingsError ? (
            <>
              <p className="text-sm text-muted-foreground">
                Couldn&apos;t load scheduling settings.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetchSettings()}>
                Retry
              </Button>
            </>
          ) : (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          )}
        </div>
      </ScheduleWorkspaceShell>
    );
  }

  return (
    <ScheduleWorkspaceShell hideHeader>
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-background">
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b bg-background px-3 py-2 sm:px-6">
          <div className="flex min-w-[260px] flex-[1_1_360px] flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                className="size-9 shrink-0 lg:size-8"
                onClick={() => setDate(toDateString(subDays(dateObj, 1)))}
              >
                <ChevronLeft className="size-4" />
              </Button>
              <Button
                variant={isToday ? 'default' : 'outline'}
                size="sm"
                className="h-9 shrink-0 px-3 text-xs lg:h-8"
                onClick={() => setDate(todayInTz(businessTz))}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-9 shrink-0 lg:size-8"
                onClick={() => setDate(toDateString(addDays(dateObj, 1)))}
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
            <div className="flex min-w-0 flex-col">
              <h1 className="min-w-0 truncate text-base font-semibold">
                {format(dateObj, 'EEEE, MMMM d, yyyy')}
              </h1>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                {routeSummaries.length} {routeSummaries.length === 1 ? 'route' : 'routes'}
              </p>
              {businessTz && showViewerHint ? (
                <p className="text-[11px] text-muted-foreground">
                  Times in {formatTimeZoneLabel(businessTz)}
                  {' · now '}
                  {formatDateTimeInTimeZone(nowIso, businessTz, {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}{' '}
                  {timeZoneAbbrev(businessTz)} (
                  {formatDateTimeInTimeZone(nowIso, viewerTz, {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}{' '}
                  your time)
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex min-w-0 flex-[1_1_320px] flex-wrap items-center justify-end gap-2">
            <Select
              value={search.territoryId ?? 'all'}
              onValueChange={(v) => setFacet('territoryId', v === 'all' ? undefined : v)}
            >
              <SelectTrigger className="h-8 w-[190px] max-w-full text-xs" aria-label="Territory">
                <SelectValue placeholder="All territories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All territories</SelectItem>
                {territories.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canManageWeatherHold ? (
              <Button
                variant={weatherHoldOpen ? 'secondary' : 'outline'}
                size="sm"
                className="h-8 gap-1.5 px-3 text-xs"
                onClick={handleOpenWeatherHold}
              >
                <CloudRain className="size-3.5" />
                Weather hold
              </Button>
            ) : null}
            <Tabs value={routesTab} onValueChange={(v) => setRoutesTab(v as RoutesTab)}>
              <TabsList className="h-8">
                <TabsTrigger value="overview" className="px-3 text-xs">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="map" className="px-3 text-xs">
                  Map
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {weatherHoldOpen && canManageWeatherHold ? (
            <WeatherHoldFlowPanel
              date={currentDate}
              bookings={routeMapStops}
              territories={territories}
              colorBy={colorBy}
              selectedBookingId={search.bookingId}
              initialTerritoryId={search.territoryId}
              drawnRing={weatherDrawRing}
              drawActive={weatherDrawActive}
              timezone={businessTz}
              onStartDraw={handleStartWeatherDraw}
              onCancelDraw={() => setWeatherDrawActive(false)}
              onClearDraw={() => setWeatherDrawRing(null)}
              onDrawAreaComplete={handleWeatherDrawComplete}
              onSelectBooking={handleBookingClick}
              onClose={handleCloseWeatherHold}
            />
          ) : routesTab === 'overview' ? (
            <RoutesOverview
              summaries={routeSummaries}
              bookings={routeMapStops}
              timezone={businessTz}
              isLoading={routeSummariesLoading}
              onSelectRoute={(technicianId) => {
                setSelectedRouteTechnicianId(technicianId);
                void navigate({ search: (prev) => ({ ...prev, routesTab: 'map' }) });
              }}
            />
          ) : (
            <RoutesMapView
              summaries={routeSummaries}
              bookings={routeMapStops}
              colorBy={colorBy}
              timezone={businessTz}
              selectedTechnicianId={activeRouteTechnicianId}
              selectedBookingId={search.bookingId}
              routeDate={currentDate}
              isToday={isToday}
              onSelectRoute={setSelectedRouteTechnicianId}
              onSelectBooking={handleBookingClick}
              weatherDrawActive={canManageWeatherHold ? weatherDrawActive : false}
              onWeatherDrawComplete={canManageWeatherHold ? handleWeatherDrawComplete : undefined}
            />
          )}
        </div>
      </div>

      <BookingDetailSheet
        booking={selectedBooking}
        open={detailOpen}
        onOpenChange={handleDetailOpenChange}
      />
    </ScheduleWorkspaceShell>
  );
}
